import { refDebounced, useCssVar } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { PostEmployeeList } from '../services/Employees/PostEmployeeList';
import { GetEmployeeList } from '../services/Employees/GetEmployeeList';
import { GetDepartmentLookups } from '../services/Lookups/GetDepartmentLookups';
import { dragToScroll } from '../helpers/dragToScroll';
import { getNode } from '@formkit/core';

export const useBulkUtilityStore = defineStore('bulkUtility', () => {
  const bulkListPagination = ref({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const bulkList = ref([]);
  const isBulkListLoading = ref(false);
  /* Filters Drawer */
  const filtersDrawer = ref(true);
  watch(filtersDrawer, (newFiltersDrawer) => {
    if (!newFiltersDrawer) {
      selectFieldsDrawer.value = false;
    }
  });
  /* Select Fields */
  const selectFieldsDrawer = ref(false);
  const searchSelectFields = ref('');
  const employmentStatusOptions = ref([
    { label: 'Regular', value: '1' },
    { label: 'Maternity', value: '2' },
    { label: 'Paternity', value: '3' },
    { label: 'Sabbatical', value: '4' },
    { label: 'Probationary', value: '8' },
    { label: 'Part-Time', value: '9' },
    { label: 'Extended Part-Time', value: '10' },
    { label: 'Contractual/Project Based', value: '11' },
    { label: 'OJT', value: '12' },
    { label: 'On PIP', value: '13' },
  ]);
  watch(selectFieldsDrawer, (newSelectFieldsDrawer, prevSelectFieldsDrawer) => {
    if (newSelectFieldsDrawer && selectedFields.value.length > 0) {
      tempSelectedFields.value = [...selectedFields.value];
    }
    if (!newSelectFieldsDrawer) {
      searchSelectFields.value = '';
    }
    if (prevSelectFieldsDrawer) {
      tempSelectedFields.value = [];
    }
  });
  const selectedFields = ref([]);
  const selectedFieldsLength = computed(() => selectedFields.value.length);
  const tempSelectedFields = ref([]);
  const selectField = (field) => {
    const found = findFieldsFromSelection(field);
    let fieldObj = field;
    if (!found) {
      if (fieldObj.advancedFilter) {
        fieldObj.advancedFilter.isActive = true;
      }
      tempSelectedFields.value.push({ ...fieldObj });
    } else {
      tempSelectedFields.value = tempSelectedFields.value.map((tempField) => {
        if (tempField.id === fieldObj.id) {
          if (tempField.advancedFilter) {
            tempField.advancedFilter.isActive = false;
            tempField.advancedFilter.isSelected = false;
          }
        }
        return tempField;
      });
      tempSelectedFields.value = tempSelectedFields.value.filter(
        (f) => f.id !== field.id
      );
    }
  };
  const findFieldsFromSelection = (field) => {
    return tempSelectedFields.value.find((d) => d.id === field.id);
  };
  const selectedFieldsDisplay = computed(() => {
    if (selectedFields.value.length > 0) {
      return `${selectedFields.value.length} field${
        selectedFields.value.length > 1 ? 's' : ''
      } selected`;
    }
    return 'Select Fields';
  });
  const confirmFieldSelection = () => {
    selectedFields.value = [...tempSelectedFields.value].sort((a, b) => {
      if (a.text > b.text) {
        return 1;
      }
      if (a.text < b.text) {
        return -1;
      }
      return 0;
    });
    selectFieldsDrawer.value = false;
    filtersDrawer.value = true;
  };
  const confirmSelectedField = ref([]);
  const colCount = useCssVar('--bulk-utility-column-count');
  watch(confirmSelectedField, (newValue) => {
    colCount.value = newValue.length;
  });
  const confirmSelectedFieldEnabled = computed(() =>
    confirmSelectedField.value.filter((field) => field.isEnabled)
  );
  const hasUniqueConfirmSelectedField = computed(() => {
    return !confirmSelectedField.value.every((x) => !x.isUnique);
  });
  /* Advanced Filters */
  const advancedFiltersDrawer = ref(false);
  const selectedAdvancedFilter = ref({});
  const searchAdvancedFilters = ref('');
  const isSelectedAdvancedFilterEmpty = computed(() => {
    return !Object.keys(selectedAdvancedFilter.value).length;
  });
  watch(advancedFiltersDrawer, (newValue) => {
    if (
      newValue &&
      selectedAdvancedFilter.value.advancedFilter.confirmedSelected?.length > 0
    ) {
      selectedAdvancedFilter.value.advancedFilter.temporarySelected = [
        ...selectedAdvancedFilter.value.advancedFilter.confirmedSelected,
      ];
    }
    if (!newValue) {
      searchAdvancedFilters.value = '';
    }
    updateAdvancedFilters();
  });
  const updateAdvancedFilters = () => {
    selectedFields.value = selectedFields.value.map((field) => {
      if (field.id === selectedAdvancedFilter.value.id) {
        field = selectedAdvancedFilter.value;
      }
      return field;
    });
  };
  const advancedFiltersList = computed(() => {
    return selectedFields.value.filter(
      (field) => field.advancedFilter?.isActive
    );
  });
  /* Departments and Immediate Supersisors Fields */
  const immediateSupervisorText = ref('');
  const departments = ref([]);
  const immediateSupervisorsPaginate = ref([]);
  const getDepartmentLookups = async () => {
    const response = await GetDepartmentLookups({
      CompanyId: selectedCompanyId.value,
    });
    departments.value = [...response.data].sort((a, b) => {
      const aDepartmentName = a.departmentName.toLowerCase();
      const bDepartmentName = b.departmentName.toLowerCase();
      if (aDepartmentName > bDepartmentName) {
        return 1;
      }
      if (aDepartmentName < bDepartmentName) {
        return -1;
      }
      return 0;
    });
    departments.value = departments.value.map((department) => {
      return {
        ...department,
        id: department.id?.toString(),
      };
    });
  };
  const isFeatureFlagOn = ref(false);
  /* Company Filter */
  const companyList = ref([]);
  const selectedCompanyId = ref('');
  const selectedCompanyName = ref('');
  watch(selectedCompanyId, async (newVal) => {
    const getCompany = companyList.value.find((val) => val.id === newVal);
    selectedCompanyName.value = getCompany.companyName;
    resetReportEmployee();
    await getEmployees();
    await getDepartmentLookups();
    if (bulkUtilityEmployeeList.value.length === 0) {
      isEmployeeListFirstLoadEmpty.value = true;
    } else {
      isEmployeeListFirstLoadEmpty.value = false;
    }
  });
  const isCompanyLoading = ref(true);
  const confirmedCompanyId = ref();
  /* Employee Filter */
  const bulkUtilityEmployeeList = ref([]);
  const selectedEmployees = ref([]);
  const selectedEmployeesText = computed(() => {
    if (
      isCompanyLoading.value ||
      (bulkUtilityEmployeeListLoader.value &&
        selectedEmployees.value.length == 0)
    ) {
      return 'Loading';
    }
    if (isEmployeeListFirstLoadEmpty.value) {
      return 'No Employee Found';
    }
    if (selectedEmployees.value.length === 0) {
      return 'All';
    }
    return `${selectedEmployees.value.length} employee${
      selectedEmployees.value.length > 1 ? 's' : ''
    } selected`;
  });
  const searchEmployee = ref('');
  const isEmployeeListFirstLoadEmpty = ref(false);
  const bulkUtilityEmployeeListLoader = ref(false);
  const debouncedSearchEmployee = refDebounced(searchEmployee, 500);
  watch(debouncedSearchEmployee, async (newVal) => {
    bulkUtilityEmployeeListLoader.value = true;
    bulkUtilityEmployeeList.value = [];
    if (newVal.length != 0) {
      bulkUtilityEmployeeListCurrentPage.value = 1;
      const params = {
        Fullname: newVal,
        PageNumber: 1,
        ...advancedFilterParams.value,
      };
      await getEmployees(params);
    } else {
      await getEmployees();
    }
  });
  const getEmployees = async (params) => {
    bulkUtilityEmployeeListLoader.value = true;
    const postEmployeeListResponse = await PostEmployeeList({
      CompanyId: selectedCompanyId.value,
      SortColumn: 'lastName',
      PageNumber: 1,
      RowsPerPage: 10,
      IsActiveEmployees: false,
      ...(params ? { ...params } : {}),
    });
    const response = await GetEmployeeList({
      Id: postEmployeeListResponse.searchCriteriaId,
    });
    if (response.data.length === 0) {
      bulkUtilityEmployeeList.value = [];
      isEmployeeListFirstLoadEmpty.value = true;
    } else {
      const mapEmployeeWorkInfo = response.data.map((d) => d.basicInformation);
      bulkUtilityEmployeeList.value =
        bulkUtilityEmployeeList.value.concat(mapEmployeeWorkInfo);
      isEmployeeListFirstLoadEmpty.value = false;
    }
    bulkUtilityEmployeeListTotalPages.value = response.pagination.totalPages;
    bulkUtilityEmployeeListLoader.value = false;
  };
  const bulkUtilityEmployeeListCurrentPage = ref(1);
  const bulkUtilityEmployeeListTotalPages = ref(0);
  const resetReportEmployee = () => {
    searchEmployee.value = '';
    selectedEmployees.value = [];
    bulkUtilityEmployeeList.value = [];
    bulkUtilityEmployeeListCurrentPage.value = 1;
    bulkUtilityEmployeeListTotalPages.value = 0;
    immediateSupervisorsPaginate.value = [];
  };
  /* Table */
  const tableRef = ref(); // For Dragging of Table
  const isSelectedAll = ref(false);
  const selectedList = ref([]);
  watch(
    isSelectedAll,
    (newValue) => {
      if (
        (newValue && selectedList.value.length !== 1) ||
        selectedList.value.length > 1
      ) {
        toggleUniqueConfirmSelectedField(true);
      } else if (!newValue && selectedList.value.length === 0) {
        toggleUniqueConfirmSelectedField(false);
      }
    },
    { deep: true }
  );
  watch(
    selectedList,
    (newVal, prevVal) => {
      if (newVal.length > 1) {
        toggleUniqueConfirmSelectedField(true);
      } else if (prevVal.length > 1 && newVal.length === 1) {
        toggleUniqueConfirmSelectedField(false);
      }
    },
    { deep: true }
  );
  let toggleUniqueConfirmSelectedField = (toggle) => {
    let val = confirmSelectedField.value.map((field) => {
      let newVal = field.isUnique && toggle ? '' : field.value;
      let node = getNode('biometricid');

      let isFieldEnabled;
      if (field.isUnique) {
        isFieldEnabled = !toggle;
      }

      if (!field.isUnique) {
        isFieldEnabled = field.isEnabled;
      }
      if (!isFieldEnabled && node) {
        node.setErrors(['']);
      }

      return {
        ...field,
        isEnabled: isFieldEnabled,
        value: newVal,
      };
    });

    confirmSelectedField.value = val;
  };
  const tableHeader = computed(() =>
    confirmSelectedField.value.map((field) => {
      return { ...field, fieldName: field.id, value: '' };
    })
  );
  const getAdvancedFilterParams = (filterText) => {
    const foundAdvancedFilterObj = advancedFiltersList.value.find(
      (filter) => filter.text === filterText
    );
    return foundAdvancedFilterObj
      ? foundAdvancedFilterObj.advancedFilter.confirmedSelected
      : [];
  };
  const advancedFilterParams = computed(() => {
    return {
      departmentIds: getAdvancedFilterParams('Department'),
      employmentStatusIds: getAdvancedFilterParams('Employment Status'),
      reportsToIds: getAdvancedFilterParams('Immediate Supervisor'),
    };
  });
  const setNewEmployeeDropdownList = async () => {
    selectedEmployees.value = [];
    bulkUtilityEmployeeList.value = [];
    bulkUtilityEmployeeListCurrentPage.value = 1;
    const params = { PageNumber: 1, ...advancedFilterParams.value };
    await getEmployees(params);
  };
  const bulkUtilityGetEmployeeList = async (params, fromPage = false) => {
    isBulkListLoading.value = true;
    const employeeSystemIds = selectedEmployees.value.map((d) => d.systemId);
    const defaultParams = {
      Include: categoriesIncluded.value,
      CompanyId: selectedCompanyId.value,
      systemIds: employeeSystemIds,
      PageNumber: bulkListPagination.value.currentPage,
      RowsPerPage: bulkListPagination.value.itemsPerPage,
      IsActiveEmployees: false,
    };
    const postEmployeeListResponse = await PostEmployeeList({
      ...defaultParams,
      ...params,
      ...advancedFilterParams.value,
    });
    const getEmployeeListResponse = await GetEmployeeList({
      Id: postEmployeeListResponse.searchCriteriaId,
    });
    bulkListPagination.value = getEmployeeListResponse.pagination;
    bulkList.value = getEmployeeListResponse.data.map((employee) => {
      return {
        ...employee,
        id: employee.basicInformation.systemId,
        isSelected: false,
        fullName: `${employee.basicInformation.lastName}, ${employee.basicInformation.firstName}`,
        sections: [
          {
            section: 'WorkInformation',
            field: tableHeader.value,
          },
        ],
        isSaved: false,
      };
    });
    if (!fromPage) {
      selectedList.value = [];
    }
    isBulkListLoading.value = false;
    dragToScroll(tableRef);
  };
  const categoriesIncluded = computed(() => {
    return fieldsCategory.value.map((field) => field.replace(' ', ''));
  });
  const selectedListCounter = computed(() => selectedList.value.length);
  /* Edit Fields*/
  const fieldsCategory = ref(['Work Information', 'Government Information']);
  const editFieldsDrawer = ref(false);
  const workInfoAccessStatus = ref();
  const governmentInfoAccessStatus = ref();
  /* Permission Check */
  const permissionCheckDrawer = ref(false);
  const hasConfirmedSelectedField = (sectionName) => {
    return confirmSelectedField.value.some(
      (field) => field.listName === sectionName
    );
  };
  /* Saved Changes */
  const confirmedWorkInfoObject = computed(() =>
    mapWorkInfoObject(confirmSelectedField.value)
  );
  const confirmedGovernmentInfoObject = computed(() =>
    mapGovernmentInfoObject(confirmSelectedField.value)
  );
  const mapWorkInfoObject = (fieldList) => {
    let obj = {};
    let enabledFields = fieldList.filter(
      (selectedField) =>
        selectedField.listName === 'Work Information' && selectedField.isEnabled
    );

    if (enabledFields.length === 0) {
      return null;
    } else {
      obj = getEnabledWorkInfoObject(enabledFields);
    }
    return obj;
  };
  const getEnabledWorkInfoObject = (enabledFields) => {
    let obj = {};
    enabledFields.forEach((field) => {
      if (!field.isEnabled) return;
      switch (field.id) {
        case 'billability': {
          obj[field.id] =
            field.value && field.value !== '' ? Number(field.value) : 2;
          break;
        }
        case 'employmentStatus': {
          obj['employmentStatusId'] = Number(field.value);
          break;
        }
        case 'payrollRunType': {
          if (field.value !== '') {
            const payrollRunTypeName = [
              { label: 'Hourly', value: '1' },
              { label: 'Daily', value: '2' },
              { label: 'Semi-Monthly', value: '3' },
              { label: 'Monthly', value: '4' },
            ].find((pRType) => pRType.value === field.value)?.label;

            obj['payrollRunType'] = {
              id: Number(field.value),
              name: payrollRunTypeName,
            };
          } else {
            obj['payrollRunType'] = {
              id: 0,
              name: '',
            };
          }
          break;
        }
        case 'immediateSupervisor': {
          obj['reportsToId'] = field.value;
          const immediateSupervisor = immediateSupervisorText.value.split(', ');
          obj[
            'reportsTo'
          ] = `${immediateSupervisor[1]} ${immediateSupervisor[0]}`;
          break;
        }
        case 'department': {
          obj['departmentId'] = field.value;
          const department = departments.value.find(
            (dept) => dept.id == field.value
          );
          obj['department'] = department.departmentName;
          break;
        }
        default: {
          obj[field.id] = field.value ? field.value : '';
          break;
        }
      }
    });
    return obj;
  };
  const mapGovernmentInfoObject = (fieldList) => {
    let obj = {};
    let enabledFields = fieldList.filter(
      (selectedField) =>
        selectedField.listName === 'Government Information' &&
        selectedField.isEnabled
    );

    if (enabledFields.length === 0) {
      return null;
    } else {
      enabledFields.forEach((field) => {
        if (!field.isEnabled) return;
        obj[field.id] = field.value ? field.value : '';
      });
    }
    return obj;
  };
  const loadingEmployees = ref([]);
  const loadingEmployeesTotal = ref();
  const successUploadToast = ref(false);
  const validationErrorsList = ref([]);
  const setValidationErrorsList = (errors) => {
    let errorsList = errors.reduce((acc, curr) => {
      let item = curr._data?.employeesdto;
      if (item) {
        let errorDataList = filteredValidationErrorsList(item);
        if (errorDataList.length > 0 && !acc.includes(item)) {
          if (
            selectedList.value.length > 1 ||
            errors.length > 1 ||
            (selectedList.value.length === 1 && errors.length === 1)
          ) {
            acc.push(curr);
          }
        }
      }
      return acc;
    }, []);
    validationErrorsList.value = errorsList;
  };
  const filteredValidationErrorsList = (item) => {
    let errorDataList = item.filter((error) => {
      let errorData = error._data;
      if (errorData?.workinformation) {
        const keys = Object.keys(errorData?.workinformation);
        return (
          keys.length > 1 ||
          (keys.length === 1 && !isKeyIncludedInSelectedFields(keys[0])) ||
          (selectedList.value.length > 1 &&
            keys.length === 1 &&
            isKeyIncludedInSelectedFields(keys[0])) ||
          (selectedList.value.length === 1 &&
            keys.length === 1 &&
            isKeyIncludedInSelectedFields(keys[0]))
        );
      }
      if (errorData?.governmentinformation) {
        const keys = Object.keys(errorData?.governmentinformation);
        return keys.length > 0;
      }
      return false;
    });
    return errorDataList;
  };
  const isKeyIncludedInSelectedFields = (key) => {
    return confirmSelectedField.value
      .map((field) => {
        return field.id.toLowerCase();
      })
      .includes(keyIncluded(key));
  };
  const keyIncluded = (key) => {
    if (key === 'biometricid') {
      return key;
    } else return key.split('id')[0];
  };
  const resetValidationErrors = () => {
    validationErrorsList.value = [];
  };
  /* Repair Dependent Fields */
  const repairDependentFieldModal = ref(false);
  const listToRepair = computed(() => {
    return selectedList.value.filter((list) => list.toBeRepair);
  });
  const totalListNotRepaired = computed(() => {
    let totalUnrepaired = 0;
    listToRepair.value.forEach((list) => {
      let unRepairedEmpoyeeCounter = 0;
      Object.entries(list.toBeRepair).forEach(([, val]) => {
        let unRepairedFieldsCounter = 0;
        val.forEach((field) => {
          if (!field.isRepaired) unRepairedFieldsCounter++;
        });
        if (unRepairedFieldsCounter > 0) {
          unRepairedEmpoyeeCounter++;
        }
      });
      if (unRepairedEmpoyeeCounter > 0) {
        totalUnrepaired++;
      }
    });
    return totalUnrepaired;
  });
  /* Disregard Repair Fields Modal */
  const disregardRepairModal = ref(false);
  const proceedToDisregardFields = ref(false);
  const selectedFieldToToggle = ref({});
  return {
    filtersDrawer,
    bulkListPagination,
    bulkList,
    isBulkListLoading,
    selectFieldsDrawer,
    searchSelectFields,
    employmentStatusOptions,
    selectedFields,
    departments,
    immediateSupervisorsPaginate,
    immediateSupervisorText,
    confirmSelectedField,
    confirmSelectedFieldEnabled,
    hasUniqueConfirmSelectedField,
    selectedAdvancedFilter,
    advancedFiltersDrawer,
    updateAdvancedFilters,
    advancedFiltersList,
    isSelectedAdvancedFilterEmpty,
    searchAdvancedFilters,
    selectedFieldsLength,
    selectField,
    tempSelectedFields,
    findFieldsFromSelection,
    selectedFieldsDisplay,
    confirmFieldSelection,
    companyList,
    selectedCompanyId,
    selectedCompanyName,
    isCompanyLoading,
    confirmedCompanyId,
    bulkUtilityEmployeeList,
    selectedEmployees,
    selectedEmployeesText,
    searchEmployee,
    isEmployeeListFirstLoadEmpty,
    bulkUtilityEmployeeListLoader,
    getEmployees,
    bulkUtilityEmployeeListCurrentPage,
    bulkUtilityEmployeeListTotalPages,
    tableRef,
    isSelectedAll,
    bulkUtilityGetEmployeeList,
    categoriesIncluded,
    selectedList,
    selectedListCounter,
    fieldsCategory,
    editFieldsDrawer,
    workInfoAccessStatus,
    governmentInfoAccessStatus,
    permissionCheckDrawer,
    hasConfirmedSelectedField,
    mapWorkInfoObject,
    mapGovernmentInfoObject,
    confirmedWorkInfoObject,
    confirmedGovernmentInfoObject,
    loadingEmployees,
    loadingEmployeesTotal,
    successUploadToast,
    validationErrorsList,
    setValidationErrorsList,
    resetValidationErrors,
    repairDependentFieldModal,
    listToRepair,
    totalListNotRepaired,
    disregardRepairModal,
    proceedToDisregardFields,
    selectedFieldToToggle,
    isFeatureFlagOn,
    setNewEmployeeDropdownList,
    advancedFilterParams,
  };
});
